import { HttpBackend, HttpClient } from '@angular/common/http';
import { computed, Injectable, Signal, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { DEFAULT_TERMINOLOGY_SECTOR } from '../../constants/terminology';
import {
  TerminologyDictionary,
  TerminologyKey,
  TerminologyLoadResult,
  TerminologyParams,
  TerminologyResolveOptions,
} from '../../models/terminology.interface';
import {
  interpolateTerm,
  isTerminologyDictionary,
  lookupTerm,
  resolveSectorCatalog,
  terminologyAssetUrl,
} from './terminology.utils';

@Injectable({
  providedIn: 'root',
})
export class TerminologyService {
  private readonly http: HttpClient;
  private readonly cache = new Map<string, TerminologyDictionary>();
  private requestSeq = 0;

  private readonly termsState = signal<TerminologyDictionary>({});
  private readonly activeSectorState = signal<string | null>(null);
  private readonly loadingState = signal(false);
  private readonly loadErrorState = signal<string | null>(null);
  private readonly revisionState = signal(0);

  /** Reactive dictionary currently applied to the UI. */
  readonly terms: Signal<TerminologyDictionary> = this.termsState.asReadonly();
  readonly activeSector: Signal<string | null> = this.activeSectorState.asReadonly();
  readonly loading: Signal<boolean> = this.loadingState.asReadonly();
  readonly loadError: Signal<string | null> = this.loadErrorState.asReadonly();
  /** Increments on every successful catalog swap; useful as a pure-pipe dependency. */
  readonly revision: Signal<number> = this.revisionState.asReadonly();

  constructor(httpBackend: HttpBackend) {
    // HttpBackend bypasses auth/error interceptors so static catalogs
    // can load before login and never attach a Bearer token.
    this.http = new HttpClient(httpBackend);
  }

  /**
   * Loads `assets/terminology/{sector}.json` and replaces the in-memory catalog.
   * Unknown sectors fall back to `default.json`. Concurrent calls are last-write-wins.
   */
  async loadSectorTerms(sector: string): Promise<TerminologyLoadResult> {
    const catalog = resolveSectorCatalog(sector);
    const requestId = ++this.requestSeq;
    const cached = this.cache.get(catalog);

    if (cached && this.activeSectorState() === catalog) {
      return {
        sector,
        catalog,
        terms: cached,
        fromCache: true,
        usedFallback: false,
      };
    }

    if (cached) {
      this.commit(catalog, cached, requestId);
      return {
        sector,
        catalog,
        terms: cached,
        fromCache: true,
        usedFallback: false,
      };
    }

    this.loadingState.set(true);
    this.loadErrorState.set(null);

    try {
      const terms = await this.fetchCatalog(catalog);
      this.cache.set(catalog, terms);
      this.commit(catalog, terms, requestId);
      return {
        sector,
        catalog,
        terms,
        fromCache: false,
        usedFallback: false,
      };
    } catch (primaryError) {
      if (catalog !== DEFAULT_TERMINOLOGY_SECTOR) {
        try {
          const fallback = await this.loadCachedOrFetch(DEFAULT_TERMINOLOGY_SECTOR);
          this.commit(DEFAULT_TERMINOLOGY_SECTOR, fallback, requestId);
          this.loadErrorState.set(
            `No se encontró terminología para "${sector}". Se usó la predeterminada.`,
          );
          return {
            sector,
            catalog: DEFAULT_TERMINOLOGY_SECTOR,
            terms: fallback,
            fromCache: this.cache.has(DEFAULT_TERMINOLOGY_SECTOR),
            usedFallback: true,
          };
        } catch {
          this.fail(requestId, primaryError);
          return this.currentResult(sector, true);
        }
      }

      this.fail(requestId, primaryError);
      return this.currentResult(sector, catalog === DEFAULT_TERMINOLOGY_SECTOR);
    }
  }

  /**
   * Resolves a terminology key safely.
   * Missing keys return `fallback` when provided, otherwise the original key.
   */
  resolve(
    key: TerminologyKey | null | undefined,
    fallbackOrOptions?: string | TerminologyResolveOptions,
    params?: TerminologyParams,
  ): string {
    this.termsState();

    if (key == null || key.trim() === '') {
      return this.readFallback(fallbackOrOptions) ?? '';
    }

    const options = this.normalizeResolveArgs(fallbackOrOptions, params);
    const raw = lookupTerm(this.termsState(), key);
    const template = raw ?? options.fallback ?? key;
    return interpolateTerm(template, options.params);
  }

  has(key: TerminologyKey): boolean {
    return lookupTerm(this.termsState(), key) != null;
  }

  computedTerm(
    key: TerminologyKey,
    fallbackOrOptions?: string | TerminologyResolveOptions,
    params?: TerminologyParams,
  ): Signal<string> {
    return computed(() => this.resolve(key, fallbackOrOptions, params));
  }

  hydrate(catalog: string, terms: TerminologyDictionary): void {
    const resolved = resolveSectorCatalog(catalog);
    this.cache.set(resolved, terms);
    this.commit(resolved, terms, ++this.requestSeq);
  }

  clear(): void {
    this.cache.clear();
    this.termsState.set({});
    this.activeSectorState.set(null);
    this.loadingState.set(false);
    this.loadErrorState.set(null);
    this.revisionState.update((value) => value + 1);
  }

  private async loadCachedOrFetch(catalog: string): Promise<TerminologyDictionary> {
    const cached = this.cache.get(catalog);
    if (cached) {
      return cached;
    }

    const terms = await this.fetchCatalog(catalog);
    this.cache.set(catalog, terms);
    return terms;
  }

  private fetchCatalog(catalog: string): Promise<TerminologyDictionary> {
    return firstValueFrom(this.http.get<unknown>(terminologyAssetUrl(catalog))).then((payload) => {
      if (!isTerminologyDictionary(payload)) {
        throw new Error(`El catálogo "${catalog}" no tiene un formato JSON válido.`);
      }
      return payload;
    });
  }

  private commit(catalog: string, terms: TerminologyDictionary, requestId: number): void {
    if (requestId !== this.requestSeq) {
      return;
    }

    this.termsState.set(terms);
    this.activeSectorState.set(catalog);
    this.loadingState.set(false);
    this.loadErrorState.set(null);
    this.revisionState.update((value) => value + 1);
  }

  private fail(requestId: number, error: unknown): void {
    if (requestId !== this.requestSeq) {
      return;
    }

    this.loadingState.set(false);
    this.loadErrorState.set(
      error instanceof Error ? error.message : 'Error al cargar la terminología.',
    );
  }

  private currentResult(sector: string, usedFallback: boolean): TerminologyLoadResult {
    return {
      sector,
      catalog: this.activeSectorState() ?? DEFAULT_TERMINOLOGY_SECTOR,
      terms: this.termsState(),
      fromCache: false,
      usedFallback,
    };
  }

  private normalizeResolveArgs(
    fallbackOrOptions?: string | TerminologyResolveOptions,
    params?: TerminologyParams,
  ): TerminologyResolveOptions {
    if (typeof fallbackOrOptions === 'string' || fallbackOrOptions == null) {
      return { fallback: fallbackOrOptions, params };
    }

    return {
      fallback: fallbackOrOptions.fallback,
      params: fallbackOrOptions.params ?? params,
    };
  }

  private readFallback(fallbackOrOptions?: string | TerminologyResolveOptions): string | undefined {
    if (typeof fallbackOrOptions === 'string') {
      return fallbackOrOptions;
    }

    return fallbackOrOptions?.fallback;
  }
}
